import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle2, AlertCircle, Download, Play, XCircle, FileText, AlertTriangle, Clock, DollarSign, TrendingUp, RefreshCw, Copy, CreditCard, Info, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { RealDataUploader } from './RealDataUploader';
import { AnalysisHistoryPanel } from './AnalysisHistoryPanel';
import { getModuleConfig } from '@/lib/modules/registry';
import { formatWithUTC } from '@/lib/datetime';
import jsPDF from 'jspdf';

interface Document {
  id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  module_type?: string;
  storage_path: string;
}

interface GenericBatchAnalysisModuleProps {
  auditId: string;
  moduleType: string;
  documents?: Document[];
  onDocumentDeleted?: () => void;
  onAnalysisStarted?: (revisionId: string) => void;
}

export function GenericBatchAnalysisModule({ auditId, moduleType, documents, onDocumentDeleted, onAnalysisStarted }: GenericBatchAnalysisModuleProps) {
  const { toast } = useToast();
  const moduleConfig = getModuleConfig(moduleType);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const deleteDocument = async (documentId: string) => {
    try {
      const { data: docData, error: fetchError } = await supabase
        .from('audit_documents')
        .select('storage_path')
        .eq('id', documentId)
        .single();

      if (fetchError) throw fetchError;

      const { error: storageError } = await supabase.storage
        .from('audit-documents')
        .remove([docData.storage_path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('audit_documents')
        .delete()
        .eq('id', documentId);

      if (dbError) throw dbError;

      toast({
        title: "Dokument raderat",
        description: "Dokumentet har tagits bort permanent"
      });

      if (onDocumentDeleted) {
        onDocumentDeleted();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Fel vid radering",
        description: "Kunde inte radera dokumentet",
        variant: "destructive"
      });
    }
  };

  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [fieldMapping, setFieldMapping] = useState<Record<string, string> | null>(null);
  const [findings, setFindings] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [premiumReport, setPremiumReport] = useState<string | null>(null);
  const [expandedFindings, setExpandedFindings] = useState<Set<number>>(new Set());
  const [companySize, setCompanySize] = useState<'small' | 'large'>('small');
  const [vatReportingPeriod, setVatReportingPeriod] = useState<'monthly' | 'quarterly'>('monthly');
  const [toleranceDays, setToleranceDays] = useState<number>(3);
  
  // Helper function to get icon based on category
  const getCategoryIcon = (category: string) => {
    const iconMap: Record<string, any> = {
      'late_payment': AlertTriangle,
      'interest': Clock,
      'fee': DollarSign,
      'large_deviation': TrendingUp,
      'irregular_pattern': TrendingUp,
      'partial_payment': RefreshCw,
      'double_payment': Copy,
      'credit': CreditCard,
      'informative': Info,
    };
    return iconMap[category] || AlertCircle;
  };

  const toggleFindingDetails = (idx: number) => {
    setExpandedFindings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };
  
  // Realtidsstatistik
  const [stats, setStats] = useState<{
    queuedBatches: number;
    processingBatches: number;
    completedBatches: number;
    failedBatches: number;
    totalTokens: number;
    totalCost: number;
  } | null>(null);

  const handleDataReady = (fileId: string, mapping: Record<string, string>, fileName: string) => {
    console.log('[GenericBatchAnalysisModule] Data ready:', { fileId, mappingKeys: Object.keys(mapping).length, fileName });
    setUploadedFileId(fileId);
    setFieldMapping(mapping);
    setFileName(fileName);
  };

  const startAnalysis = async () => {
    if (!uploadedFileId || !fieldMapping) {
      toast({
        title: "Fel",
        description: "Ingen fil uppladdad eller fältmappning saknas",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsAnalyzing(true);
      console.log('[GenericBatchAnalysisModule] Starting direct analysis...');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Användare inte inloggad');
      }
      
      // Convert Record<string, string> to array format for edge function
      const mappingArray = Object.entries(fieldMapping).map(([mappedName, originalName]) => ({
        originalName,
        mappedName,
        confidence: 1.0,
        isRequired: ['Datum', 'Händelse', 'Belopp'].includes(mappedName)
      }));
      
      console.log('[GenericBatchAnalysisModule] Field mapping array:', mappingArray);
      
      const { data, error } = await supabase.functions.invoke('start-backend-worker', {
        body: {
          fileId: uploadedFileId,
          fieldMapping: mappingArray,
          companySize,
          vatReportingPeriod,
          toleranceDays,
          auditId,
          moduleType,
          userId: user.id,
          fileName: fileName || 'Okänd fil',
        }
      });

      if (error) {
        console.error('[GenericBatchAnalysisModule] Analysis failed:', error);
        toast({
          title: "Fel vid analys",
          description: error.message || "Analysen misslyckades",
          variant: "destructive",
        });
        setIsAnalyzing(false);
        return;
      }

        console.log('[GenericBatchAnalysisModule] Analysis started:', data);

        setIsAnalyzing(false);

        toast({
          title: "Analys påbörjad",
          description: `Analysen körs i bakgrunden. Du kommer automatiskt till Analyser-fliken för att följa framstegen.`,
          duration: 8000,
        });

        // Notifiera parent component för automatisk navigation
        if (onAnalysisStarted && data.revision_id) {
          onAnalysisStarted(data.revision_id);
        }

    } catch (err) {
      console.error('[GenericBatchAnalysisModule] Error during analysis:', err);
      toast({
        title: "Fel",
        description: err instanceof Error ? err.message : "Okänt fel vid analys",
        variant: "destructive",
      });
      setIsAnalyzing(false);
    }
  };

  const clearActiveAnalysis = () => {
    setUploadedFileId(null);
    setFileName('');
    setFieldMapping(null);
    setFindings([]);
    setSummary({});
    setPremiumReport(null);
    setIsAnalyzing(false);

    toast({
      title: "Analys rensad",
      description: "Du kan nu starta en ny analys",
    });
  };

  const pollStats = async () => {
    try {
      // Hämta aktiva revisioner för denna audit + moduleType
      const { data: revisions, error: revisionsError } = await supabase
        .from('revisions')
        .select('id')
        .eq('audit_id', auditId)
        .eq('module_type', moduleType)
        .eq('status', 'processing');

      if (revisionsError) throw revisionsError;
      if (!revisions || revisions.length === 0) {
        setStats(null);
        return;
      }

      const revisionIds = revisions.map(r => r.id);

      // Räkna batchar per status
      const { data: batches, error: batchesError } = await supabase
        .from('batches')
        .select('state')
        .in('revision_id', revisionIds);

      if (batchesError) throw batchesError;

      const queuedBatches = batches?.filter(b => b.state === 'queued').length || 0;
      const processingBatches = batches?.filter(b => b.state === 'processing').length || 0;
      const completedBatches = batches?.filter(b => b.state === 'completed').length || 0;
      const failedBatches = batches?.filter(b => b.state === 'failed').length || 0;

      // ✅ NYTT: Dölj statistik om inga batchar är aktiva längre
      const activeBatches = queuedBatches + processingBatches;
      if (activeBatches === 0 && batches && batches.length > 0) {
        console.log('[GenericBatchAnalysisModule] Alla batchar klara, döljer statistik');
        setStats(null);
        return;
      }

      // Hämta aggregerad statistik
      const { data: aggregates, error: aggregatesError } = await supabase
        .from('aggregates')
        .select('*')
        .in('revision_id', revisionIds);

      if (aggregatesError) throw aggregatesError;

      const totalTokens = revisions.reduce((sum, r: any) => sum + (r.total_tokens_in || 0) + (r.total_tokens_out || 0), 0);
      const totalCost = revisions.reduce((sum, r: any) => sum + (r.total_cost_sek || 0), 0);

      setStats({
        queuedBatches,
        processingBatches,
        completedBatches,
        failedBatches,
        totalTokens,
        totalCost,
      });
    } catch (err) {
      console.error('[pollStats] Error:', err);
    }
  };

  // Poll stats när isAnalyzing är true
  useEffect(() => {
    let interval: number | undefined;
    if (isAnalyzing) {
      pollStats(); // Initial call
      interval = window.setInterval(pollStats, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAnalyzing]);

  const generatePremiumReport = async () => {
    if (findings.length === 0) {
      toast({
        title: "Inga fynd",
        description: "Det finns inga fynd att generera rapport från",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGeneratingReport(true);
      console.log('[GenericBatchAnalysisModule] Generating premium report...');

      const { data, error } = await supabase.functions.invoke('generate-module-report', {
        body: {
          findings,
          summary,
          moduleType,
          companyName: null, // Kan läggas till senare om vi vill
        }
      });

      if (error) {
        console.error('[GenericBatchAnalysisModule] Report generation failed:', error);
        toast({
          title: "Fel vid rapportgenerering",
          description: error.message || "Kunde inte generera rapport",
          variant: "destructive",
        });
        return;
      }

      console.log('[GenericBatchAnalysisModule] Premium report generated successfully');
      setPremiumReport(data.report);

      toast({
        title: "Rapport genererad",
        description: "Premium-rapporten har skapats",
      });

    } catch (err) {
      console.error('[GenericBatchAnalysisModule] Error generating report:', err);
      toast({
        title: "Fel",
        description: err instanceof Error ? err.message : "Okänt fel vid rapportgenerering",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const doc = new jsPDF();
      let yPos = 20;

      doc.setFontSize(18);
      doc.text(moduleConfig.name, 20, yPos);
      yPos += 15;

      doc.setFontSize(12);
      doc.text(`Totalt antal fynd: ${findings.length}`, 20, yPos);
      yPos += 10;

      const criticalCount = findings.filter(f => f.severity === 'critical').length;
      const highCount = findings.filter(f => f.severity === 'high').length;
      const mediumCount = findings.filter(f => f.severity === 'medium').length;
      const lowCount = findings.filter(f => f.severity === 'low').length;

      doc.text(`Kritiska: ${criticalCount}`, 20, yPos);
      yPos += 7;
      doc.text(`Höga: ${highCount}`, 20, yPos);
      yPos += 7;
      doc.text(`Medel: ${mediumCount}`, 20, yPos);
      yPos += 7;
      doc.text(`Låga: ${lowCount}`, 20, yPos);
      yPos += 15;

      doc.setFontSize(14);
      doc.text('Fynd:', 20, yPos);
      yPos += 10;

      doc.setFontSize(10);
      findings.forEach((finding, index) => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFont('helvetica', 'bold');
        const titleText = finding.score !== undefined 
          ? `${index + 1}. ${finding.severity.toUpperCase()} (Score: ${finding.score})`
          : `${index + 1}. ${finding.severity.toUpperCase()}`;
        doc.text(titleText, 20, yPos);
        yPos += 7;

        doc.setFont('helvetica', 'normal');
        const messageLines = doc.splitTextToSize(finding.message || 'Ingen beskrivning', 170);
        messageLines.forEach((line: string) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 25, yPos);
          yPos += 7;
        });

        if (finding.affectedPeriod) {
          doc.text(`Period: ${finding.affectedPeriod}`, 25, yPos);
          yPos += 7;
        }

        if (finding.details) {
          const detailLines = doc.splitTextToSize(`Detaljer: ${finding.details}`, 170);
          detailLines.forEach((line: string) => {
            if (yPos > 270) {
              doc.addPage();
              yPos = 20;
            }
            doc.text(line, 25, yPos);
            yPos += 7;
          });
        }

        yPos += 5;
      });

      doc.save(`${moduleConfig.name.replace(/\s+/g, '_')}_rapport.pdf`);

      toast({
        title: "PDF genererad",
        description: "Rapporten har laddats ner",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Fel vid PDF-generering",
        description: "Kunde inte skapa PDF-rapport",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{moduleConfig.name}</CardTitle>
          <CardDescription>{moduleConfig.description}</CardDescription>
        </CardHeader>
      </Card>

      {/* Visa uppladdade dokument om de finns */}
      {documents && documents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Uppladdade dokument ({documents.filter(d => d.module_type === moduleType).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {documents
                .filter(doc => doc.module_type === moduleType)
                .map((doc) => (
                  <div key={doc.id} className="border rounded-lg p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <FileText className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 className="h-4 w-4 text-success" />
                          <h4 className="font-medium">{doc.original_filename}</h4>
                          <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/20">
                            Uppladdat
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatFileSize(doc.size_bytes)} • {formatWithUTC(doc.created_at)}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="gap-2 text-destructive hover:text-destructive-foreground hover:bg-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                              Radera
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Radera dokument</AlertDialogTitle>
                              <AlertDialogDescription>
                                Är du säker på att du vill radera "{doc.original_filename}"? 
                                Denna åtgärd kan inte ångras.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => deleteDocument(doc.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Radera
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {!uploadedFileId && !isAnalyzing && findings.length === 0 && (
        <RealDataUploader
          customerId={auditId}
          moduleName={moduleType}
          onDataReady={handleDataReady}
        />
      )}

      {uploadedFileId && fieldMapping && !isAnalyzing && findings.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Redo att starta analys</CardTitle>
            <CardDescription>
              Fil: {fileName} | Fältmappningar: {Object.keys(fieldMapping).length}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Företagsstorlek</label>
                <select
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value as 'small' | 'large')}
                  className="w-full mt-1 p-2 border rounded"
                >
                  <option value="small">Litet företag (≤49 anställda)</option>
                  <option value="large">Stort företag (≥50 anställda)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Momsredovisning</label>
                <select
                  value={vatReportingPeriod}
                  onChange={(e) => setVatReportingPeriod(e.target.value as 'monthly' | 'quarterly')}
                  className="w-full mt-1 p-2 border rounded"
                >
                  <option value="monthly">Månadsvis</option>
                  <option value="quarterly">Kvartalsvis</option>
                </select>
              </div>
            </div>
            
            {/* Tolerans för sena betalningar */}
            <div className="space-y-2">
              <label className="text-sm font-medium block">Tolerans för sena betalningar</label>
              <div className="flex items-center space-x-4">
                <input 
                  type="range"
                  min="0"
                  max="7"
                  value={toleranceDays}
                  onChange={(e) => setToleranceDays(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
                <div className="flex items-center space-x-2 min-w-[120px]">
                  <span className="text-2xl font-bold text-primary">{toleranceDays}</span>
                  <span className="text-sm text-muted-foreground">
                    {toleranceDays === 1 ? 'dag' : 'dagar'}
                  </span>
                </div>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Strikt (0)</span>
                <span>Standard (3)</span>
                <span>Flexibel (7)</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {toleranceDays === 0 
                  ? 'Ingen tolerans - alla sena betalningar flaggas omedelbart'
                  : `Betalningar flaggas som sena först ${toleranceDays} ${toleranceDays === 1 ? 'dag' : 'dagar'} efter förfallodatum. AI:n identifierar alla avvikelser, men de inom toleransen filtreras bort automatiskt.`
                }
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={startAnalysis} className="flex-1">
                <Play className="h-4 w-4 mr-2" />
                Starta analys
              </Button>
              <Button variant="outline" onClick={clearActiveAnalysis}>
                <XCircle className="h-4 w-4 mr-2" />
                Rensa
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isAnalyzing && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Analyserar {moduleConfig.name}...</span>
              <Button
                variant="outline"
                size="sm"
                onClick={clearActiveAnalysis}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Avbryt
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Bearbetar</Badge>
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
            <Progress value={undefined} className="animate-pulse" />
          </CardContent>
        </Card>
      )}

      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>Realtidsstatistik</CardTitle>
            <CardDescription>Live-uppdatering av analysprocessen</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-sm font-medium">I kö</div>
                <div className="text-2xl font-bold">{stats.queuedBatches}</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">Processar</div>
                <div className="text-2xl font-bold">{stats.processingBatches}</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">Klara</div>
                <div className="text-2xl font-bold text-green-600">{stats.completedBatches}</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm font-medium">Fel</div>
                <div className="text-2xl font-bold text-red-600">{stats.failedBatches}</div>
              </div>
            </div>
            
            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between text-sm">
                <span>Totalt batchar:</span>
                <span className="font-medium">
                  {stats.completedBatches} / {stats.queuedBatches + stats.processingBatches + stats.completedBatches + stats.failedBatches}
                </span>
              </div>
              <Progress 
                value={
                  ((stats.completedBatches / 
                    (stats.queuedBatches + stats.processingBatches + stats.completedBatches + stats.failedBatches || 1)) * 100)
                } 
              />
            </div>

            {stats.totalTokens > 0 && (
              <div className="pt-4 border-t space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Totala tokens:</span>
                  <span className="font-medium">{stats.totalTokens.toLocaleString()}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isAnalyzing && findings.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Analys slutförd
                </CardTitle>
                <CardDescription className="mt-2">
                  Hittade {findings.length} fynd
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant={premiumReport ? "default" : "outline"} 
                  onClick={generatePremiumReport} 
                  disabled={isGeneratingReport}
                >
                  {isGeneratingReport ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  {premiumReport ? "Uppdatera rapport" : "Generera rapport"}
                </Button>
                <Button variant="outline" onClick={generatePDF} disabled={isGeneratingPDF}>
                  {isGeneratingPDF ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  PDF
                </Button>
                <Button variant="outline" onClick={clearActiveAnalysis}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Ny analys
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Kritiska:</span>
                <span className="ml-2 font-medium text-red-600">
                  {findings.filter(f => f.severity === 'critical').length}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Höga:</span>
                <span className="ml-2 font-medium text-orange-600">
                  {findings.filter(f => f.severity === 'high').length}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Medel:</span>
                <span className="ml-2 font-medium text-yellow-600">
                  {findings.filter(f => f.severity === 'medium').length}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Låga:</span>
                <span className="ml-2 font-medium text-blue-600">
                  {findings.filter(f => f.severity === 'low').length}
                </span>
              </div>
            </div>

            {!premiumReport ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Analysen identifierade <strong>{findings.length}</strong> observationer. 
                  Generera en premium-rapport för detaljerad analys.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['critical', 'high', 'medium', 'low', 'info'].map(severity => {
                    const count = findings.filter(f => f.severity === severity).length;
                    if (count === 0) return null;
                    return (
                      <Badge 
                        key={severity}
                        variant={
                          severity === 'critical' || severity === 'high' 
                            ? 'destructive' 
                            : severity === 'medium' 
                              ? 'default' 
                              : 'secondary'
                        }
                      >
                        {count} {severity}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {findings.map((finding, idx) => {
                  const CategoryIcon = getCategoryIcon(finding.category);
                  const isExpanded = expandedFindings.has(idx);
                  const hasDetails = finding.details && (typeof finding.details === 'object' || finding.details.length > 0);
                  
                  return (
                    <Alert key={idx} variant={finding.severity === 'critical' || finding.severity === 'high' ? 'destructive' : 'default'}>
                      <CategoryIcon className="h-4 w-4" />
                      <AlertTitle className="flex items-center gap-2 flex-wrap">
                        <Badge variant={
                          finding.severity === 'critical' ? 'destructive' :
                          finding.severity === 'high' ? 'destructive' :
                          finding.severity === 'medium' ? 'default' :
                          'secondary'
                        }>
                          {finding.severity}
                        </Badge>
                        {finding.category && (
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <CategoryIcon className="h-3 w-3" />
                            {finding.category}
                          </span>
                        )}
                      </AlertTitle>
                      <AlertDescription className="mt-2 space-y-2">
                        <p className="font-medium">{finding.message}</p>
                        
                        {hasDetails && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleFindingDetails(idx)}
                            className="h-auto py-1 px-2 text-xs"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp className="h-3 w-3 mr-1" />
                                Dölj tekniska detaljer
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-3 w-3 mr-1" />
                                Visa tekniska detaljer
                              </>
                            )}
                          </Button>
                        )}
                        
                        {isExpanded && hasDetails && (
                          <div className="mt-2 p-3 bg-muted/50 rounded-md border border-border">
                            <p className="text-xs text-muted-foreground font-mono whitespace-pre-wrap">
                              {typeof finding.details === 'string' 
                                ? finding.details 
                                : JSON.stringify(finding.details, null, 2)}
                            </p>
                          </div>
                        )}
                      </AlertDescription>
                    </Alert>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Visa premium-rapporten om den är genererad */}
      {premiumReport && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Premium Revisionsrapport
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-slate max-w-none">
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: premiumReport
                    // Fetstil **text** → <strong>text</strong>
                    .replace(/\*\*\s*(.*?)\s*\*\*/g, '<strong>$1</strong>')
                    // H2 headers med visuell stil
                    .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-8 mb-4 pb-2 border-b-2 border-slate-200 dark:border-slate-700">$1</h2>')
                    // H3 subheaders
                    .replace(/^### (.+)$/gm, '<h3 class="text-xl font-semibold text-slate-800 dark:text-slate-200 mt-6 mb-3">$1</h3>')
                    // Bullets med spacing
                    .replace(/^- (.+)$/gm, '<li class="ml-4 mb-2 text-slate-700 dark:text-slate-300">$1</li>')
                    // Paragraphs
                    .replace(/^([^<#-].+)$/gm, '<p class="mb-4 text-slate-700 dark:text-slate-300 leading-relaxed">$1</p>')
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visa historik när det finns tidigare analyser */}
      <AnalysisHistoryPanel auditId={auditId} moduleType={moduleType} />
    </div>
  );
}
